/* eslint-disable @typescript-eslint/no-unused-vars */
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FieldValues } from 'react-hook-form';
import { useEffect, useMemo, useRef, useState } from 'react';
import localForage from 'localforage';
import classnames from 'classnames';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { useQueryClient } from '@tanstack/react-query';
import { ListItemIcon } from '@mui/material';

import { PolicyRouteType } from '../modals/policy/ModalPolicy';
import ManageCookies from '../modal-cookies/ModalCookies';
import ModalContainer from '../common/modal-container/modal-container';
import { isWebViewContainer } from '../../utils/platform.util';
import { logData } from '../../utils/debug.util';
import { setPolicyRoute } from '../../store/slices/generalSlice';
import { RootState } from '../../store/rootReducer';
import storageService from '../../services/storage.service';
import { AssistanceInformation, IPostponedSurvey } from '../../models/assistance.model';
import { getSupportedLanguages, INSURANCE_COMPANIES, Language } from '../../constants';
import closeIcon from '../../assets/icons/close-icon.svg';
import BurgerIcon from '../../assets/icons/burger-icon.svg';
import NavItem, { menuItemI } from './nav-link/NavItem';
import { IMenuProps } from './menu-types';
import { MenuStyle } from './menu-style';

type MenuRoute = {
  title?: any;
  onClick?: () => void;
  route?: string;
  items?: menuItemI[];
  classNames?: string;
};

const Menu: React.FC<IMenuProps> = ({ classNames }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [renderModal, setRenderModal] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownHeight, setDropdownHeight] = useState<number>(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(['common']);
  const supportedLanguages = getSupportedLanguages(t);
  const targetRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const assistanceData = queryClient.getQueryData<AssistanceInformation>(['assitanceInformation']);
  const postponedSurvey = queryClient.getQueryData<IPostponedSurvey>(['postponedSurvey']);

  const providerCode = useSelector((state: RootState) => state.general.provider);
  const shouldShowLOPD = useMemo(
    () => providerCode && INSURANCE_COMPANIES?.[providerCode] && providerCode !== '00000016',
    [providerCode],
  );

  const isWebView = useMemo(() => isWebViewContainer(), []);

  useEffect(() => {
    logData(`${isWebView ? 'WebView' : 'Movile/Desktop'} para ${providerCode}`);
  }, [isWebView, providerCode]);

  const selectPolicyUrl = (route: PolicyRouteType | undefined) => {
    if (!providerCode) return undefined;
    if (route === 'privacy') {
      return INSURANCE_COMPANIES?.[providerCode]?.privacy;
    } else if (route === 'cookies') {
      return INSURANCE_COMPANIES?.[providerCode]?.cookies;
    }
    return INSURANCE_COMPANIES?.[providerCode]?.legal;
  };

  const handlePolicyModal = (route: PolicyRouteType | undefined) => {
    const url = selectPolicyUrl(route);
    if (!isWebView && providerCode === '00000002' && route !== 'lopd') {
      window.open(url, '_blank');
    } else {
      dispatch(setPolicyRoute({ policy: route }));
    }
  };

  const onHomeClick = async () => {
    const form: FieldValues | null = await localForage.getItem('assistanceFormNoLogin');

    const assistanceUrl = await storageService.getItem<string>('assistanceUrl');

    if (assistanceUrl) {
      navigate(assistanceUrl);
      return;
    }

    if (!form) {
      navigate('/');
      return;
    } else {
      const step: number | null = await localForage.getItem<number>('unregisteredStep');
      if (step && step >= 9 && assistanceData?.identifier && assistanceData?.intervention) {
        navigate(`/unregistered/${assistanceData?.intervention}/${assistanceData?.identifier}`);
        setIsOpen(false);
        return;

        // navigate('/unregistered');
      }
      navigate('/unregistered');
    }
  };

  useEffect(() => {
    if (dropdownRef.current) {
      setDropdownHeight(dropdownRef.current.offsetHeight);
    }
  }, [dropdownOpen, dropdownRef]);

  useEffect(() => {
    if (isOpen) {
      disableBodyScroll(targetRef.current as HTMLElement);
    } else {
      enableBodyScroll(targetRef.current as HTMLElement);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [isOpen]);

  const unregisteredRoutes: MenuRoute[] = [
    { title: t('common:menu.home'), onClick: onHomeClick },
    ...(postponedSurvey?.file && postponedSurvey?.intervention
      ? [
          {
            title: t('common:menu.pending-survey'),
            route: `/survey/${postponedSurvey?.file}/${postponedSurvey?.intervention}`,
          },
        ]
      : []),
    { title: t('common:menu.contact'), route: '/contact' },
    {
      classNames: 'language-item',
      title: (
        <>
          <p>{t('common:menu.language')}</p>
          <ListItemIcon>
            <img src={supportedLanguages?.[i18n.language as Language]?.icon} alt="" />
          </ListItemIcon>
        </>
      ),
      items: [
        {
          title: t('common:languages.spanish'),
          onClick: () => i18n.changeLanguage('es'),
        },
        {
          title: t('common:languages.english'),
          onClick: () => i18n.changeLanguage('en'),
        },
        {
          title: t('common:languages.catalan'),
          onClick: () => i18n.changeLanguage('ca'),
        },
      ],
    },
  ];

  return (
    <>
      <MenuStyle className={classNames}>
        <div
          className={classnames('burger', { active: !!isOpen })}
          data-cy="menu-burger"
          onClick={() => setIsOpen((current) => !current)}
        >
          <img src={BurgerIcon} alt="" />
        </div>

        <div className={classnames('menu-background', { active: !!isOpen })}>
          <div className={classnames('menu-container', { active: !!isOpen })}>
            <div className="close-button">
              <div className="close-button__icon" data-cy="menu-close" onClick={() => setIsOpen((current) => !current)}>
                <img src={closeIcon} alt="" />
              </div>
            </div>
            <div className="nav-links" ref={targetRef}>
              {unregisteredRoutes.map((item, index: number) => (
                <div
                  key={`nav-link-${index}`}
                  onClick={() => {
                    if (item?.onClick) {
                      item.onClick();
                    } else if (item.items === undefined) {
                      setIsOpen(false);
                    }
                  }}
                >
                  <NavItem
                    classNames={item.classNames}
                    closeMenu={() => setIsOpen(false)}
                    route={item.route}
                    title={item.title}
                    submenuItems={item.items ? item.items : []}
                  />
                </div>
              ))}
              <div className="nav-links-bottom">
                <button
                  className="nav-links-bottom__item"
                  data-cy="menu-privacy"
                  onClick={() => {
                    return handlePolicyModal('privacy');
                  }}
                >
                  <p>{t('common:menu.footer.privacy')}</p>
                </button>
                {shouldShowLOPD && (
                  <button
                    className="nav-links-bottom__item"
                    data-cy="menu-lopd"
                    onClick={() => handlePolicyModal('lopd')}
                  >
                    <p>{t('common:menu.footer.lopd')}</p>
                  </button>
                )}
                <button
                  className="nav-links-bottom__item"
                  data-cy="menu-cookies"
                  onClick={() => handlePolicyModal('cookies')}
                >
                  <p>{t('common:menu.footer.cookies')}</p>
                </button>
                <button
                  className="nav-links-bottom__item"
                  data-cy="menu-legal"
                  onClick={() => handlePolicyModal('legal')}
                >
                  <p>{t('common:menu.footer.legal')}</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      </MenuStyle>
      <ManageCookies />
    </>
  );
};

export default Menu;
